import { useCallback, useEffect, useState } from 'react';

import { useIntl } from 'react-intl';

import type { ContextIdentifierProvider } from '@atlaskit/editor-common/provider-factory';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import type {
	AiManagedObject,
	AiManagedObjectConfigCreateInput,
} from '@atlassian/ai-managed-objects/types';
import type { ValidationErrorCode } from '@atlassian/ai-managed-objects/validation';
import { validateManagedBlockConfig } from '@atlassian/ai-managed-objects/validation';

import type {
	AiManagedObjectLoadStatus,
	AgentManagedExtensionAttributes,
	AgentManagedExtensionTemplateConfig,
} from '../../common/agentManagedExtensionPluginType';
import { messages } from '../../common/messages';
import { getReferenceObjectFromContextIdentifier } from '../../common/referenceObjectAri';
import { getNodeAiManagedObject } from '../../common/utils';
import {
	createAiManagedObjectWithCache,
	updateAiManagedObjectWithCache,
} from '../ai-managed-object-cache';
import {
	dispatchAgentManagedExtensionMeta,
	findConfiguredAgentManagedExtension,
	type AgentManagedExtensionNodeWithPos,
} from '../editor-commands/agent-managed-object-node';

import { type ManagedBlockConfiguration, hasExecutableConfigurationChanged } from './configuration';
import { useAiManagedExecution } from './useAiManagedExecution';
import { useAiManagedObjectConfigurationLoad } from './useAiManagedObjectConfigurationLoad';

type UseAiManagedObjectArgs = {
	aimoId?: string;
	aimoLoadStatus?: AiManagedObjectLoadStatus;
	cachedAimo?: AiManagedObject;
	cloudId?: string;
	configLocalId?: string;
	contentType?: string;
	contextIdentifierProvider?: ContextIdentifierProvider;
	editorView: EditorView;
	foundPos?: number;
	isOpen: boolean;
	templateConfig?: AgentManagedExtensionTemplateConfig;
};

type UseAiManagedObjectResult = {
	aimoData: AiManagedObject | null;
	configurationLoadError: string | null;
	error: string | null;
	handleCancel: () => void;
	handleClearError: () => void;
	handleRetryConfigurationLoad: () => void;
	handleSave: (config: AiManagedObjectConfigCreateInput) => Promise<void>;
	initialConfiguration: ManagedBlockConfiguration;
	isLoadingConfiguration: boolean;
	isSaving: boolean;
};

type ConfiguredExtensionSaveTarget = {
	currentId?: string;
	localId?: string;
};

const validationErrorMessageKeys: Record<ValidationErrorCode, keyof typeof messages> = {
	EMPTY_PROMPT: 'emptyPrompt',
	INVALID_DESIRED_UPDATE_FREQUENCY: 'invalidDesiredUpdateFrequency',
	MISSING_GOAL: 'missingGoal',
	MISSING_OUTPUT_FORMAT: 'missingOutputFormat',
	MISSING_SOURCES: 'missingSources',
};

const getSaveTarget = (
	currentFound: AgentManagedExtensionNodeWithPos,
): ConfiguredExtensionSaveTarget => {
	const attrs = currentFound.node.attrs as AgentManagedExtensionAttributes;
	return {
		currentId: getNodeAiManagedObject(attrs)?.id,
		localId: currentFound.node.attrs.localId as string | undefined,
	};
};

const saveAiManagedObject = async ({
	cloudId,
	config,
	contentType,
	contextObjectId,
	contextProduct,
	currentId,
	editorView,
	localId,
}: {
	cloudId?: string;
	config: AiManagedObjectConfigCreateInput;
	contentType?: string;
	contextObjectId?: string;
	contextProduct?: string;
	currentId?: string;
	editorView: EditorView;
	localId?: string;
}): Promise<AiManagedObject | null> => {
	if (currentId) {
		return updateAiManagedObjectWithCache(
			editorView,
			{
				id: currentId,
				config,
			},
			localId,
			{ type: 'CLOSE_CONFIG' },
		);
	}

	const referenceObject = getReferenceObjectFromContextIdentifier({
		cloudId,
		contentType,
		objectId: contextObjectId,
		product: contextProduct,
	});
	if (!referenceObject) {
		return null;
	}

	return createAiManagedObjectWithCache(
		editorView,
		{
			cloudId: referenceObject.cloudId,
			referenceObjectAri: referenceObject.referenceObjectAri,
			referenceProduct: 'CONFLUENCE',
			status: 'ACTIVE',
			type: 'BLOCK',
			config,
		},
		localId,
		{ type: 'CLOSE_CONFIG' },
	);
};

export const useAiManagedObject = ({
	aimoId,
	aimoLoadStatus,
	cachedAimo,
	cloudId,
	configLocalId,
	contextIdentifierProvider,
	contentType,
	editorView,
	foundPos,
	isOpen,
	templateConfig,
}: UseAiManagedObjectArgs): UseAiManagedObjectResult => {
	const intl = useIntl();
	const saveErrorMessage = intl.formatMessage(messages.saveError);
	const [isSaving, setIsSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const { executeAiManagedObject } = useAiManagedExecution({ editorView });
	const contextObjectId = contextIdentifierProvider?.objectId;
	const contextProduct = contextIdentifierProvider?.product;
	const {
		aimoData,
		configurationLoadError,
		handleRetryConfigurationLoad,
		initialConfiguration,
		isLoadingConfiguration,
		setAimoData,
	} = useAiManagedObjectConfigurationLoad({
		aimoId,
		aimoLoadStatus,
		cachedAimo,
		configLocalId,
		editorView,
		foundPos,
		isOpen,
		templateConfig,
	});

	useEffect(() => {
		if (!isOpen || foundPos === undefined) {
			setError(null);
			setIsSaving(false);
			return;
		}

		if (!aimoId || cachedAimo) {
			setError(null);
		}
	}, [aimoId, cachedAimo, foundPos, isOpen]);

	const handleSave = useCallback(
		async (config: AiManagedObjectConfigCreateInput): Promise<void> => {
			const validation = validateManagedBlockConfig(config);
			if (!validation.valid) {
				const validationMessageKey = validation.errors[0]?.code
					? validationErrorMessageKeys[validation.errors[0].code]
					: undefined;

				setError(
					validationMessageKey
						? intl.formatMessage(messages[validationMessageKey])
						: saveErrorMessage,
				);
				return;
			}

			setIsSaving(true);
			setError(null);

			try {
				const currentFound = findConfiguredAgentManagedExtension(editorView, configLocalId);
				if (!currentFound) {
					setError(saveErrorMessage);
					setIsSaving(false);
					return;
				}

				const { currentId, localId } = getSaveTarget(currentFound);
				const shouldExecuteAfterSave = hasExecutableConfigurationChanged(aimoData?.config, config);
				const aimo = await saveAiManagedObject({
					cloudId,
					config,
					contentType,
					contextObjectId,
					contextProduct,
					currentId,
					editorView,
					localId,
				});

				if (!aimo) {
					setError(saveErrorMessage);
					setIsSaving(false);
					return;
				}

				setAimoData(aimo);
				setIsSaving(false);

				if (!shouldExecuteAfterSave) {
					return;
				}

				const executedAimo = await executeAiManagedObject({
					aimoId: aimo.id,
					localId,
				});
				if (executedAimo) {
					setAimoData(executedAimo);
				}
			} catch {
				setError(saveErrorMessage);
				setIsSaving(false);
			}
		},
		[
			aimoData?.config,
			cloudId,
			configLocalId,
			contentType,
			contextObjectId,
			contextProduct,
			editorView,
			executeAiManagedObject,
			intl,
			setAimoData,
			saveErrorMessage,
		],
	);

	const handleClearError = useCallback((): void => {
		setError(null);
	}, []);

	const handleCancel = useCallback((): void => {
		setError(null);
		setIsSaving(false);
		dispatchAgentManagedExtensionMeta(editorView, { type: 'CLOSE_CONFIG' });
		editorView.focus();
	}, [editorView]);

	return {
		aimoData: aimoData,
		configurationLoadError,
		error: error,
		handleCancel: handleCancel,
		handleClearError: handleClearError,
		handleRetryConfigurationLoad,
		handleSave: handleSave,
		initialConfiguration,
		isLoadingConfiguration,
		isSaving: isSaving,
	};
};
